<script setup>
import {onBeforeMount, onMounted, provide, reactive, ref} from 'vue'
import {Feedback, clone, State} from '@/paks/vu-app'
import {getView, getViewChildren, getViewDir, removeView, updateView} from './BoardUtils.js'
import PageNavEdit from './PageNavEdit.vue'
import PageAppearanceEdit from './PageAppearanceEdit.vue'
import PageComponentEdit from './PageComponentEdit.vue'

const props = defineProps({
    modelValue: Object,
})

const emit = defineEmits(['close', 'update:modelValue'])

const ReservedPages = ['/login', '/logout', '/claim', '/designer']

const page = reactive({
    applying: false,
    askRange: false,
    canDesign: false,
    components: [],
    copyPath: '',
    existing: false,
    prior: {},
    saving: false,
    showCopy: false,
    tab: 'nav',
    view: {widgets: []},
})

//  Component refs
const form = ref(null)
const confirm = ref(null)
provide('form', form)

onBeforeMount(() => {
    let view = (page.view = clone(props.modelValue))
    page.prior = clone(view)
    page.existing = view.path ? true : false
    if (!view.role) {
        view.role = 'inherited'
    }
    if (view.component == 'Page' || view.component == 'Home') {
        page.canDesign = true
    }
    if (page.canDesign && !view.board) {
        view.board = {
            css: [],
            framed: true,
            layout: 'grid',
            toolbar: true,
            type: 'page',
            widgets: [],
            widgetCss: [],
        }
    }
})

onMounted(() => {})

async function preSave(validate) {
    let {prior, view} = page
    if (view.name == 'New View') {
        throw new Error('Must set page name')
    }
    if (prior.path != view.path || !page.existing) {
        let found = getView(view.path)
        if (found.key >= 0) {
            throw new Error('View path already in use')
        }
    }
    return true
}

async function apply(view) {
    if (view.role == 'inherited') {
        view.role = null
    }
    if (page.canDesign && !view.board) {
        view.board = {
            css: [],
            framed: false,
            layout: 'grid',
            name: view.title,
            refresh: null,
            properties: {},
            toolbar: false,
            type: 'page',
            widgets: [],
            widgetCss: [],
        }
    }
    if (page.prior.path && page.prior.path != view.path) {
        removeView(page.prior)
    }
    // REPAIR - if not parented correctly
    let found = getView(view.path)
    if (found.parent?.path && getViewDir(view.path) != found.parent.path) {
        // Remove and re-add to reparent if required
        removeView(view)
    }
    updateView(view)
    // If routes change, we need to reload the board to update the routes
    State.app.setNeedSave('display')
    State.app.setNeed('reload')
    emit('update:modelValue', page.view)
}

async function save() {
    let view = clone(page.view)
    await apply(view)
    emit('close')
}

async function copy() {
    page.showCopy = false
    let view = clone(page.view)
    view.path = page.copyPath
    updateView(view)
    State.app.setNeedSave('display')
    State.app.setNeed('reload')
    emit('update:modelValue', page.view)
    emit('close')
}

async function deleteView() {
    let view = page.view
    if (ReservedPages.includes(view.path)) {
        Feedback.error('This page is required by the framework and cannot be deleted')
        return
    }
    let msg = `Do you want to delete the page "${view.path || view.title}".`
    let count = getViewChildren(view.views).length
    if (count > 0) {
        msg += `\n\nThis page has ${count} sub-pages. If you delete this page, all of those sub-pages will also be deleted.`
    }
    if (!(await confirm.value.ask(msg))) {
        return
    }
    removeView(view)
    State.app.setNeedSave('display')
    emit('close')
}
</script>

<template>
    <vu-form
        class="page-edit"
        help="/doc/ui/view/page-edit.html"
        ref="form"
        :data="page"
        :options="{enter: false}"
        :pre-save="preSave"
        :save="save"
        :title="`${page.existing ? 'Modify' : 'Add'} Page`">
        <v-container fluid class="pa-0">
            <v-tabs v-model="page.tab" ref="tabs" class="page-tabs mb-5" fixed-tabs>
                <v-tab value="nav">Navigation</v-tab>
                <v-tab v-if="page.canDesign" value="appearance">Appearance</v-tab>
                <v-tab value="component">Component</v-tab>
            </v-tabs>

            <v-window v-model="page.tab" class="pt-4">
                <v-window-item value="nav">
                    <PageNavEdit v-model="page.view" />
                </v-window-item>
                <v-window-item v-if="page.canDesign" value="appearance">
                    <PageAppearanceEdit v-model="page.view.board" />
                </v-window-item>
                <v-window-item value="component">
                    <PageComponentEdit v-model="page.view" />
                </v-window-item>
            </v-window>
        </v-container>

        <div class="actions">
            <v-btn
                size="small"
                color="primary"
                type="submit"
                :loading="page.saving"
                :disabled="form?.value?.validate?.hasErrors()">
                Save
            </v-btn>
            <v-btn size="small" color="primary" @click="apply(page.view)" :loading="page.applying">Apply</v-btn>
            <v-btn size="small" color="primary" @click="page.showCopy = true">Copy</v-btn>
            <v-btn size="small" color="none" @click="emit('close')">Cancel</v-btn>
            <v-btn size="small" color="error" v-if="page.view.path" @click="deleteView">Delete</v-btn>
        </div>
        <vu-confirm ref="confirm" />
    </vu-form>

    <v-dialog v-model="page.showCopy" width="550" content-class="copy-dialog">
        <v-card>
            <v-card-title class="headline" primary-title>Copy Page</v-card-title>
            <v-card-text class="mt-4 pb-0">
                <label>Enter a route path for the new page</label>
            </v-card-text>
            <v-card-text>
                <v-text-field v-model="page.copyPath" single-line label="Page Path" />
            </v-card-text>
            <v-card-actions class="pb-5 px-5">
                <v-btn ripple color="primary" variant="elevated" :loading="page.saving" type="submit" @click="copy">
                    Save
                </v-btn>
                <v-btn ripple variant="elevated" @click="page.showCopy = false">Cancel</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style lang="scss">
.page-edit {
    .page-tabs {
        background: rgb(var(--v-theme-background));
        border: solid 1px rgb(var(--v-theme-border));
        .v-tab {
            border-right: solid 1px rgb(var(--v-theme-border));
        }
        .v-tab--selected {
            background: rgb(var(--v-theme-accent));
            color: white;
        }
        .v-btn {
            padding: 0 8px;
            font-size: 11px !important;
        }
    }
    .pick-view {
        margin: 16px 0 0px 0;
        width: 100%;
        .v-input__control {
            border-color: black;
            border-radius: 4px;
        }
    }
    .v-select-list {
        padding: 0;
        border: solid 1px rgb(var(--v-theme-border-lighten-1));
        border-radius: 0;
    }
    h3 {
        font-weight: normal;
        color: rgb(var(--v-theme-text));
    }
    .layouts {
        margin: 10px 0 20px 0;
    }
    .actions {
        margin: 20px 0 20px 0;
    }
    .v-list-item {
        min-height: 30px !important;
    }
    .v-list-item__content {
        padding: 6px 0 6px 0 !important;
    }
    .v-list {
        padding: 0;
    }
    .v-select__content {
        .v-list {
            background: rgb(var(--v-theme-background-lighten-1)) !important;
        }
    }
    margin-inline-start: 0 !important;
}
</style>

<style lang="scss" scoped>
.pick-items {
    font-size: 1rem;
}
.v-list-item {
    min-height: 30px !important;
}
.v-list-item__content {
    padding: 6px 0 6px 0 !important;
}
.v-list {
    padding: 0;
}
.copy-dialog {
    .headline {
        background: rgb(var(--v-theme-primary));
        color: rgb(var(--v-theme-on-primary));
        font-weight: bold;
        padding: 16px;
    }
}
</style>
